'use client'
import { Box, Flex, Skeleton, Text } from '@mantine/core'
import Image from 'next/image'
// import Location from 'public/images/location.svg'

import { RiMapPinLine, RiStarFill } from '@remixicon/react'
import dynamic from 'next/dynamic'
import { memo } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import MobilePropertyImage from './MobilePropertyImage'
import Bathrooms from 'public/images/bathrooms.svg'
import Bedrooms from 'public/images/bedrooms.svg'
import Beds from 'public/images/beds.svg'
import Guests from 'public/images/guests.svg'

export const NewBookingFormMobile = dynamic(
  () => import('../section/NewBookingFrom'),
  {
    ssr: false,
    loading: () => {
      return <Skeleton height={460} width='100%' bdrs={10} radius='xl' />
    },
  },
)

const SkeltonRating = () => {
  return (
    <div className='gap-4 mx-4'>
      {/* Star + rating skeleton */}
      <Skeleton width={81} height={38} radius='xl' className='mb-2' />

      {/* Reviews count skeleton */}
      <Skeleton
        width={70}
        height={16}
        radius='sm'
        className='mx-2 mt-4'
        style={{
          display: 'inline-block',
          verticalAlign: 'middle',
        }}
        mx={5}
        mt={4}
      />
    </div>
  )
}

const MobilePropertyDetailsCard = ({ data, loading, slug }) => {
  const router = useRouter()
  const t = useTranslations('propertydetails')
  const reviewsCount = Number(data?.reviews_count ?? 0)
  const hasReviews = reviewsCount > 0

  const handleCityClick = () => {
    const city = data?.city
    if (city) router.push(`/property-listing?location=${encodeURIComponent(city)}`)
  }

  const propertyDetails = [
    {
      id: 'guests',
      icon: Guests,
      value: data?.max_guests || 0,
      label: (Number(data?.max_guests) || 0) === 1 ? 'guest' : 'guests',
      imgWidth: 16,
      imgHeight: 16,
    },
    {
      id: 'bedrooms',
      icon: Bedrooms,
      value: data?.bedrooms || 0,
      label: (Number(data?.bedrooms) || 0) === 1 ? 'bedroom' : 'bedrooms',
      imgWidth: 20,
      imgHeight: 20,
    },
    {
      id: 'beds',
      icon: Beds,
      value: data?.beds || 0,
      label: (Number(data?.beds) || 0) === 1 ? 'bed' : 'beds',
      imgWidth: 20,
      imgHeight: 20,
    },
    {
      id: 'bathrooms',
      icon: Bathrooms,
      value: data?.bathrooms || 0,
      label: (Number(data?.bathrooms) || 0) === 1 ? 'bathroom' : 'bathrooms',
      imgWidth: 16,
      imgHeight: 16,
    },
  ]
  // Create array of valid images (skip main photo, only use other_photos)
  const validImages = []

  // Add other photos if they exist and have valid URLs
  if (data?.other_photos && Array.isArray(data.other_photos)) {
    data.other_photos.forEach((item, i) => {
      // Use optimized image URL (large_img_url -> medium_img_url -> original_img_url)
      const imageUrl =
        item?.large_img_url || item?.medium_img_url || item?.original_img_url
      if (imageUrl && !validImages?.some((img) => img.url === imageUrl)) {
        validImages.push({
          url: imageUrl,
          alt: `Other Photo ${i + 1}`,
        })
      }
    })
  }

  // Fallback: if no other photos available, show main photo
  if (validImages?.length === 0 && data?.main_photo) {
    validImages.push({
      url: data.main_photo,
      alt: 'Main Photo',
    })
  }

  return (
    <div>
      <div className='overflow-hidden'>
        <MobilePropertyImage data={data} loading={loading} slug={slug} />
      </div>

      <div className='pt-7 pb-3 px-4'>
        <h1 className='text-[22px] font-semibold pb-2.5 text-start'>
          {data?.name}
        </h1>

        {loading || !data ? (
          <Skeleton w={'100%'} h={57} />
        ) : (
          <Flex
            w={'100%'}
            h={57}
            align='center'
            wrap='wrap'
            // small inter-item spacing is handled by the separator "dot" itself
            gap={0}
            // keeps rows tight and consistent when wrapping
            style={{ rowGap: 10 }}
          >
            {propertyDetails.map((item, index) => (
              <Flex key={item.id} align='center'>
                <Flex align='center' gap={8}>
                  <Image
                    src={item.icon}
                    alt={item.label}
                    width={item.imgWidth}
                    height={item.imgHeight}
                  />
                  <Text
                    c='#191919'
                    fw={500}
                    fz={13}
                    lh='160%'
                    style={{ letterSpacing: 'normal' }}
                  >
                    {item.value} {item.label}
                  </Text>
                </Flex>

                {index !== propertyDetails.length - 1 && (
                  <Box
                    aria-hidden
                    w={4}
                    h={4}
                    bg='#CECECE'
                    mx={10}
                    style={{ borderRadius: '50%' }}
                  />
                )}
              </Flex>
            ))}
          </Flex>
        )}

        <div className='w-full opacity-100 my-4'>
          {loading || !data ? (
            <Skeleton w='100%' h={14} />
          ) : (
            <p className='text-[#191919] font-poppins font-normal text-[13px] leading-[160%] tracking-normal'>
              {data?.short_summary}
            </p>
          )}
        </div>

        {loading || !data ? (
          <SkeltonRating />
        ) : (
          <div className='flex flex-col gap-1'>
            {hasReviews ? (
              <>
                <div className='flex items-center gap-2 w-fit h-fit px-3 py-2 rounded-full bg-[#191919]'>
                  <RiStarFill className='w-4 h-4 text-[#FFA125]' />
                  <span className='font-semibold text-white text-base'>
                    {Number(data?.overall_reviews ?? 0).toFixed(1)}
                  </span>
                </div>

                <div className='font-medium text-[13px] leading-[160%] text-[#898989]'>
                  {reviewsCount} {reviewsCount === 1 ? 'review' : 'reviews'}
                </div>
              </>
            ) : (
              <div className='flex items-center gap-2 w-fit h-fit px-3 py-2 rounded-full bg-[#F6F6F6]'>
                <RiStarFill className='w-4 h-4 text-[#FFA125]' />
                <span className='font-semibold text-[#191919] text-base'>
                  {t('badges.new')}
                </span>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className='w-[167px] h-[21px] flex items-center gap-[4px] opacity-100 mt-5'>
            <Skeleton w={16} h={16} />
            <Skeleton w={120} h={16} />
          </div>
        ) : (
          <div className='w-full h-[21px] flex items-center gap-[4px] opacity-100 mt-5'>
            <RiMapPinLine className='text-[#80c7e2] w-4 h-4' />
            <button
              type='button'
              onClick={handleCityClick}
              className='text-[#5F6369] font-medium text-[13px] leading-[160%] tracking-normal hover:underline underline-offset-2 cursor-pointer'
            >
              {data?.city}, {data?.country}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default memo(MobilePropertyDetailsCard)
